import {
	Component, Input, Output, OnInit, OnDestroy, OnChanges, SimpleChanges, SimpleChange,
	EventEmitter, ChangeDetectorRef, ViewChild
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { IdentityService } from '../../services/identity.service';
import { UsersService, UsersServiceEvent, UsersServiceEventType } from '../../services/users.service';
import { MessageService } from 'base';
import {
	UserDto, GroupDto, UserClass, AccountType, LoginResultDto, LoginResultCode,
	ActiveDirectorySettingsDto, GoogleWorkspaceSettingsDto, GroupTreeDto, getAccountTypeLabel
} from 'UvExplorerServer.Dtos.TS/Auth';
import { LicensingService } from 'identity';
import { ObjSerUtils } from 'DiscoveryEngine.TS/Serialization';
import { MenuItem } from 'primeng/api';


enum DirectoryGroupOperation {
	None, Create, Edit, Sync
}


enum DelayedOperationType {
	None,
	CreateActiveDirectoryGroup,
	CreateGoogleWorkspaceGroup,
}


@Component({
    selector: 'group-list',
	templateUrl: './group-list.component.html',
	styleUrls: ['./group-list.component.scss']
})
export class GroupListComponent implements OnInit, OnDestroy, OnChanges {

 	public entries: any[];
	private groupToDelete: GroupDto;
	public confirmDeleteDialogVisible: boolean;

	public groupDialogVisible: boolean;
	public groupDialogTarget: GroupDto;
	public groupDialogCreate: boolean;

	public adAdminDialogVisible: boolean;
	public adAdminDialogDomains: string[];

	public gwAdminDialogVisible: boolean;

	public dirGroupOperation: DirectoryGroupOperation;
	public dirGroupAccountType: AccountType;
	public dirGroupTarget: GroupDto;

	public dirGroupDialogVisible: boolean;
	public dirGroupDialogDomains: string[];

	private contextGroupGuid: string;

	private adSettingsLoadedSub: Subscription;
	private gwSettingsLoadedSub: Subscription;
	private userChangeSub: Subscription;
	private adSettings: ActiveDirectorySettingsDto;
	private gwSettings: GoogleWorkspaceSettingsDto;

	public createMenuItems: MenuItem[];
	public syncMenuItems: MenuItem[];

	// When re-entering the app during OAuth flow, createDirectoryGroup was being called
	// before the directory settings had been asyncronously loaded.
	// Therefore, it was necessary to add support for delayed operations that are run
	// once the directory settings have been loaded.
	private delayedOpType: DelayedOperationType;

	constructor(private identityService: IdentityService,
		private usersService: UsersService,
		private licensingService: LicensingService,
		private messageService: MessageService) {

		this.entries = [];
		this.groupToDelete = null;
		this.confirmDeleteDialogVisible = false;

		this.resetGroupDialogState();
		this.resetAdAdminDialogState();
		this.resetGwAdminDialogState();
		this.resetDirGroupDialogState();
		this.resetContextState();

		this.adSettings = null;
		this.gwSettings = null;

		this.delayedOpType = DelayedOperationType.None;

		this.adSettingsLoadedSub = this.usersService.onActiveDirectorySettingsLoaded.subscribe(
			(result: ActiveDirectorySettingsDto) => {
				this.copyAdSettings();
				this.initMenuItems();

				if (this.delayedOpType === DelayedOperationType.CreateActiveDirectoryGroup) {
					this.delayedOpType = DelayedOperationType.None;
					this.createDirectoryGroup(AccountType.ActiveDirectory);
				}
			});

		this.gwSettingsLoadedSub = this.usersService.onGoogleWorkspaceSettingsLoaded.subscribe(
			(result: GoogleWorkspaceSettingsDto) => {
				this.copyGwSettings();
				this.initMenuItems();

				if (this.delayedOpType === DelayedOperationType.CreateGoogleWorkspaceGroup) {
					this.delayedOpType = DelayedOperationType.None;
					this.createDirectoryGroup(AccountType.GoogleWorkspace);
				}
			});

		// When a user is created, updated, or deleted, their group memberships may change,
		// which changes the modified timestamp on all affected groups. Thus, the need
		// to refresh groups here.
		this.userChangeSub = this.usersService.onUserChange.subscribe(
			(event: UsersServiceEvent) => {
				switch (event.type) {
					case UsersServiceEventType.UserCreate:
					case UsersServiceEventType.UserUpdate:
					case UsersServiceEventType.UserDelete:
						this.refreshGroups();
						break;
				}
			});
	}

	private initMenuItems(): void {
		this.initCreateMenuItems();
		this.initSyncMenuItems();
	}

	private initCreateMenuItems(): void {
		this.createMenuItems = [
			{
				icon: "fa fa-plus-square-o",
				label: "UVexplorer Group",
				command: () => this.createGroup(),
				disabled: !this.isLicenseValid,
			}
		];

		if (!!this.adSettings && this.adSettings.Enabled) {
			this.createMenuItems.push(
				{
					icon: "fa fa-plus-square-o",
					label: "Active Directory Group",
					command: () => this.createActiveDirectoryGroup(),
					disabled: !this.isLicenseValid,
				}
			);
		}

		if (!!this.gwSettings && this.gwSettings.Enabled) {
			this.createMenuItems.push(
				{
					icon: "fa fa-plus-square-o",
					label: "Google Workspace Group",
					command: () => this.createGoogleWorkspaceGroup(),
					disabled: !this.isLicenseValid,
				}
			);
		}
	}

	private initSyncMenuItems(): void {
		this.syncMenuItems = [];

		if (!!this.adSettings && this.adSettings.Enabled) {
			this.syncMenuItems.push(
				{
					icon: "fa fa-refresh",
					label: "Active Directory Group Data",
					command: () => this.syncActiveDirectoryGroups(),
					disabled: !this.isLicenseValid,
				}
			);
		}

		if (!!this.gwSettings && this.gwSettings.Enabled) {
			this.syncMenuItems.push(
				{
					icon: "fa fa-refresh",
					label: "Google Workspace Group Data",
					command: () => this.syncGoogleWorkspaceGroups(),
					disabled: !this.isLicenseValid,
				}
			);
		}
	}

	private resetGroupDialogState(): void {
		this.groupDialogVisible = false;
		this.groupDialogTarget = null;
		this.groupDialogCreate = false;
	}

	private resetAdAdminDialogState(): void {
		this.adAdminDialogVisible = false;
		this.adAdminDialogDomains = [];
	}

	private resetGwAdminDialogState(): void {
		this.gwAdminDialogVisible = false;
	}

	private resetDirGroupDialogState(): void {
		this.dirGroupAccountType = AccountType.Native;
		this.dirGroupDialogVisible = false;
		this.dirGroupTarget = null;
		this.dirGroupOperation = DirectoryGroupOperation.None;
		this.dirGroupDialogDomains = [];
	}

	private resetContextState(): void {
		this.contextGroupGuid = "";
	}

	public get hasEntries(): boolean {
		return (this.entries.length > 0);
	}

	private copyAdSettings(): void {
		if (this.usersService.hasActiveDirectorySettings) {
			this.adSettings = new ActiveDirectorySettingsDto();
			ObjSerUtils.Copy(this.usersService.adSettings, this.adSettings);
		}
		else {
			this.adSettings = null;
		}
	}

	private copyGwSettings(): void {
		if (this.usersService.hasGoogleWorkspaceSettings) {
			this.gwSettings = new GoogleWorkspaceSettingsDto();
			ObjSerUtils.Copy(this.usersService.gwSettings, this.gwSettings);
		}
		else {
			this.gwSettings = null;
		}
	}

	public ngOnInit(): void {
		this.refreshGroups();

		if (this.usersService.hasActiveDirectorySettings) {
			this.copyAdSettings();
		}
		if (this.usersService.hasGoogleWorkspaceSettings) {
			this.copyGwSettings();
		}

		this.initMenuItems();
	}

	public ngOnDestroy(): void {
		if (this.adSettingsLoadedSub) {
			this.adSettingsLoadedSub.unsubscribe();
			this.adSettingsLoadedSub = null;
		}
		if (this.gwSettingsLoadedSub) {
			this.gwSettingsLoadedSub.unsubscribe();
			this.gwSettingsLoadedSub = null;
		}
		if (this.userChangeSub) {
			this.userChangeSub.unsubscribe();
			this.userChangeSub = null;
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		
	}

	public get isLicenseValid(): boolean {
		//return this.licensingService.isLicenseValid;
		return true;
	}

	public get isActiveDirectoryEnabled(): boolean {
		return (!!this.adSettings && this.adSettings.Enabled);
	}

	public get isGoogleWorkspaceEnabled(): boolean {
		return (!!this.gwSettings && this.gwSettings.Enabled);
	}

	public get isDirGroupCreate(): boolean {
		return (this.dirGroupOperation == DirectoryGroupOperation.Create);
	}

	public refreshGroups(): void {
		if (this.identityService.isLoggedIn) {
			this.getAllGroups();
		}
	}

	public createGroup(): void {
		this.groupDialogCreate = true;
		this.groupDialogTarget = new GroupDto();
		this.groupDialogTarget.AccountType = AccountType.Native;
		this.groupDialogVisible = true;
	}

	public createActiveDirectoryGroup(): void {

		this.identityService.testAdmin(AccountType.ActiveDirectory).subscribe(
			(result: LoginResultDto) => {
				if (result.Code === LoginResultCode.Succeeded) {
					this.createDirectoryGroup(AccountType.ActiveDirectory);
				}
				else {
					this.dirGroupOperation = DirectoryGroupOperation.Create;
					this.dirGroupAccountType = AccountType.ActiveDirectory;

					this.adAdminDialogDomains = this.adSettings.DomainNames.ToArray();
					this.adAdminDialogVisible = true;
				}
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not verify Active Directory admin credentials");
			}
		);
	}

	public onAdAdminDialogOkClicked({ username: username,
		password: password, domain: domain }): void {

		this.identityService.loginActiveDirectoryAdmin(
			username, password, domain).subscribe(
				(result: LoginResultDto) => {
					if (result.Code === LoginResultCode.Succeeded) {
						console.assert(this.dirGroupAccountType == AccountType.ActiveDirectory);

						switch (this.dirGroupOperation) {
							case DirectoryGroupOperation.Create:
								this.createDirectoryGroup(AccountType.ActiveDirectory);
								break;
							case DirectoryGroupOperation.Edit:
								this.editDirectoryGroup(this.dirGroupTarget);
								break;
							case DirectoryGroupOperation.Sync:
								this.syncDirectoryGroups(AccountType.ActiveDirectory);
								break;
							default:
								console.assert(false, "Invalid directory group operation");
								break;
						}
					}
					else {
						this.resetDirGroupDialogState();
						this.messageService.displayErrorMessage("Login failed");
					}
				},
				(error) => {
					this.resetDirGroupDialogState();
					this.messageService.displayErrorMessage("Login failed");
				}
		);

		this.resetAdAdminDialogState();
	}

	public onAdAdminDialogCancelClicked(): void {
		this.resetAdAdminDialogState();
		this.resetDirGroupDialogState();
	}

	public createGoogleWorkspaceGroup(): void {

		this.identityService.testAdmin(AccountType.GoogleWorkspace).subscribe(
			(result: LoginResultDto) => {
				if (result.Code === LoginResultCode.Succeeded) {
					this.createDirectoryGroup(AccountType.GoogleWorkspace);
				}
				else {
					this.dirGroupOperation = DirectoryGroupOperation.Create;
					this.dirGroupAccountType = AccountType.GoogleWorkspace;

					this.gwAdminDialogVisible = true;
				}
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not verify Google Workspace admin credentials");
			}
		);
	}

	public onGwAdminDialogOkClicked(): void {

		let finalRedirectUrl = "";
		let errorRedirectUrl = "/admin/grouplist/loginadmin/failed";

		switch (this.dirGroupOperation) {
			case DirectoryGroupOperation.Create:
				finalRedirectUrl = location.origin + "/admin/grouplist/creategroup/googleworkspace";
				break;
			case DirectoryGroupOperation.Edit:
				finalRedirectUrl = location.origin + "/admin/grouplist/editgroup/googleworkspace/" + this.dirGroupTarget.Guid;
				break;
			case DirectoryGroupOperation.Sync:
				finalRedirectUrl = location.origin + "/admin/grouplist/syncgroups/googleworkspace";
				break;
			default:
				console.assert(false, "Invalid directory group operation");
				break;
		}

		this.identityService.loginGoogleWorkspaceAdmin(finalRedirectUrl, errorRedirectUrl).subscribe(
			(result: LoginResultDto) => {
				// Do nothing because a successful call will result in a redirect
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not initiate Google admin login");
			}
		);

		this.resetGwAdminDialogState();
	}

	public onGwAdminDialogCancelClicked(): void {
		this.resetGwAdminDialogState();
		this.resetDirGroupDialogState();
	}

	public createDirectoryGroup(groupType: AccountType): void {

		// When re-entering the app during OAuth flow, createDirectoryGroup was being called
		// before the directory settings had been asyncronously loaded.
		// Therefore, it was necessary to add support for delayed operations that are run
		// once the directory settings have been loaded.

		switch (groupType) {
			case AccountType.ActiveDirectory:
				if (!this.adSettings) {
					this.delayedOpType = DelayedOperationType.CreateActiveDirectoryGroup;
					return;
				}
				break;
			case AccountType.GoogleWorkspace:
				if (!this.gwSettings) {
					this.delayedOpType = DelayedOperationType.CreateGoogleWorkspaceGroup;
					return;
				}
				break;
			default:
				console.assert(false);
				console.error("Unknown account type");
				return;
		}

		this.dirGroupAccountType = groupType;
		this.dirGroupOperation = DirectoryGroupOperation.Create;
		this.dirGroupTarget = new GroupDto();
		this.dirGroupTarget.AccountType = groupType;
		this.dirGroupDialogVisible = true;
		this.setDirGroupDialogDomains(groupType);
	}

	private setDirGroupDialogDomains(groupType: AccountType): void {
		switch (groupType) {
			case AccountType.ActiveDirectory:
				this.dirGroupDialogDomains = this.adSettings.DomainNames.ToArray();
				break;
			case AccountType.GoogleWorkspace:
				this.dirGroupDialogDomains = this.gwSettings.DomainNames.ToArray();
				break;
			default:
				console.assert(false, "Invalid AccountType");
				break;
		}
	}

	public editGroup(group: GroupDto): void {
		if (group.AccountType == AccountType.Native) {
			this.groupDialogCreate = false;
			this.groupDialogTarget = group;
			this.groupDialogVisible = true;
		}
		else if (group.AccountType == AccountType.ActiveDirectory) {
			this.identityService.testAdmin(AccountType.ActiveDirectory).subscribe(
				(result: LoginResultDto) => {
					if (result.Code === LoginResultCode.Succeeded) {
						this.editDirectoryGroup(group);
					}
					else {
						this.dirGroupOperation = DirectoryGroupOperation.Edit;
						this.dirGroupAccountType = AccountType.ActiveDirectory;
						this.dirGroupTarget = group;

						this.adAdminDialogDomains = this.adSettings.DomainNames.ToArray();
						this.adAdminDialogVisible = true;
					}
				},
				(error) => {
					this.messageService.displayErrorMessage("Could not verify Active Directory admin credentials");
				}
			);
		}
		else if (group.AccountType == AccountType.GoogleWorkspace) {
			this.identityService.testAdmin(AccountType.GoogleWorkspace).subscribe(
				(result: LoginResultDto) => {
					if (result.Code == LoginResultCode.Succeeded) {
						this.editDirectoryGroup(group);
					}
					else {
						this.dirGroupOperation = DirectoryGroupOperation.Edit;
						this.dirGroupAccountType = AccountType.GoogleWorkspace;
						this.dirGroupTarget = group;

						this.gwAdminDialogVisible = true;
					}
				},
				(error) => {
					this.messageService.displayErrorMessage("Could not verify Google admin credentials");
				}
			);
		}
	}

	private editDirectoryGroup(group: GroupDto): void {
		this.dirGroupAccountType = group.AccountType;
		this.dirGroupOperation = DirectoryGroupOperation.Edit;
		this.dirGroupTarget = group;
		this.dirGroupDialogVisible = true;
	}

	public editDirectoryGroupByGuid(groupGuid: string): void {
		if (this.hasEntries) {
			let group: GroupDto =
				this.usersService.groupTree.Groups.ToArray().find((group: GroupDto) => group.Guid === groupGuid);
			if (group) {
				this.dirGroupAccountType = group.AccountType;
				this.dirGroupOperation = DirectoryGroupOperation.Edit;
				this.dirGroupTarget = group;
				this.dirGroupDialogVisible = true;
			}
		}
		else {
			this.contextGroupGuid = groupGuid;
		}
	}

	public deleteGroup(group: GroupDto): void {
		if (this.deleteIsSafe(group)) {
			this.groupToDelete = group;
			this.confirmDeleteDialogVisible = true;
		}
		else {
			this.messageService.displayErrorMessage("Deleting this group is not allowed");
		}
	}

	private deleteIsSafe(group: GroupDto): boolean {
		return true;
	}

	public onAdminLoginFailed(): void {
		this.messageService.displayErrorMessage("Login failed");
	}

	private getAllGroups(): void {
		this.usersService.getAllGroups().subscribe(
			(result: GroupTreeDto) => {
				this.rebuildEntries();
				if (this.contextGroupGuid) {
					this.editDirectoryGroupByGuid(this.contextGroupGuid);
				}
				this.resetContextState();
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not retrieve group information");
				this.resetContextState();
			});
	}

	private rebuildEntries(): void {
		this.entries = [];

		// Initialize entries
		this.usersService.groupTree.Groups.ForEach((group: GroupDto) => {
			if (group.HasName) {
				this.addEntry(group);
			}
		}, this);
	}

	private addEntry(group: GroupDto): void {
		if (!this.entries.some((entry) => entry.tag.Guid === group.Guid)) {
			let entry = {
				groupname: group.Name,
				class: UserClass[group.UserClass],
				type: getAccountTypeLabel(group.AccountType),
				active: (group.IsActive ? 'Yes' : 'No'),
				tag: group
			};

			this.entries.push(entry);
		}
	}

	private updateEntry(group: GroupDto): void {
		let found = this.entries.some((entry) => {
			if (entry.tag == group) {
				entry.groupname = group.Name;
				entry.class = UserClass[group.UserClass];
				entry.type = getAccountTypeLabel(group.AccountType);
				entry.active = (group.IsActive ? 'Yes' : 'No');
				return true;
			}
			else {
				return false;
			}
		});

		if (!found)
			console.error("Did not find group entry to update");
	}

	private removeEntry(group: GroupDto): void {
		for (let i = 0; i < this.entries.length; ++i) {
			if (this.entries[i].tag == group) {
				this.entries.splice(i, 1);
				break;
			}
		}
	}

	private triggerChangeDetection(): void {
		this.entries = [...this.entries];
	}

	public onGroupDialogOkClicked(group: GroupDto): void {
		if (this.groupDialogCreate) {
			// If new group is being created, append entry for it
			this.addEntry(group);
		}
		else {
			// If existing group is being updated, refresh the existing
			// entry with the group's updated properties
			this.updateEntry(group);
		}

		// Force redisplay of modified entries
		this.triggerChangeDetection();

		this.resetGroupDialogState();
	}

	public onGroupDialogCancelClicked(): void {
		this.resetGroupDialogState();
	}

	public onDirGroupDialogOkClicked(group: GroupDto): void {
		if (this.dirGroupOperation == DirectoryGroupOperation.Create) {
			// If new group is being created, append entry for it
			this.addEntry(group);
		}
		else {
			// If existing group is being updated, refresh the existing
			// entry with the group's updated properties
			this.updateEntry(group);
		}

		// Force redisplay of modified entries
		this.triggerChangeDetection();

		this.resetDirGroupDialogState();
	}

	public onDirGroupDialogCancelClicked(): void {
		this.resetDirGroupDialogState();
	}

	public onDeleteConfirmDialogOkClicked(): void {
		let groupToDelete = this.groupToDelete;
		this.groupToDelete = null;
		this.confirmDeleteDialogVisible = false;

		if (groupToDelete) {
			let parentGroup: GroupDto = this.usersService.groupTree.FindGroupParent(groupToDelete.Guid);
			if (parentGroup) {
				this.usersService.deleteGroup(groupToDelete.Guid, parentGroup.Guid).subscribe(
					(result: boolean) => {
						// Remove deleted entry
						this.removeEntry(groupToDelete);

						// Force redisplay of modified entries
						this.triggerChangeDetection();
					},
					(error) => {
						this.messageService.displayErrorMessage("Deleting group failed");
					});
			}
			else {
				this.messageService.displayErrorMessage("Could not delete the group (parent group not found)");
			}
		}
	}

	public onDeleteConfirmDialogCancelClicked(): void {
		this.groupToDelete = null;
		this.confirmDeleteDialogVisible = false;
	}

	public syncActiveDirectoryGroups(): void {

		this.identityService.testAdmin(AccountType.ActiveDirectory).subscribe(
			(result: LoginResultDto) => {
				if (result.Code === LoginResultCode.Succeeded) {
					this.syncDirectoryGroups(AccountType.ActiveDirectory);
				}
				else {
					this.dirGroupOperation = DirectoryGroupOperation.Sync;
					this.dirGroupAccountType = AccountType.ActiveDirectory;

					this.adAdminDialogDomains = this.adSettings.DomainNames.ToArray();
					this.adAdminDialogVisible = true;
				}
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not verify Active Directory admin credentials");
			}
		);

	}

	public syncGoogleWorkspaceGroups(): void {

		this.identityService.testAdmin(AccountType.GoogleWorkspace).subscribe(
			(result: LoginResultDto) => {
				if (result.Code === LoginResultCode.Succeeded) {
					this.syncDirectoryGroups(AccountType.GoogleWorkspace);
				}
				else {
					this.dirGroupOperation = DirectoryGroupOperation.Sync;
					this.dirGroupAccountType = AccountType.GoogleWorkspace;

					this.gwAdminDialogVisible = true;
				}
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not verify Google Workspace admin credentials");
			}
		);

	}

	public syncDirectoryGroups(accountType: AccountType): void {

		this.messageService.displayInfoMessage("Syncing directory groups");
		this.messageService.startWork();
		this.usersService.syncDirectoryGroups(accountType).subscribe(
			() => {
				//this.usersService.dirGroupsCache.remove(accountType);
				this.messageService.displayInfoMessage("Directory groups successfully synced");
				this.messageService.endWork();
			},
			(error) => {
				//this.usersService.dirGroupsCache.remove(accountType);
				this.messageService.displayErrorMessage("Could not sync directory groups");
				this.messageService.endWork();
			});
	}

}
